import { mergeWith, isArray, union } from 'lib';
import { magicNumbers } from 'utils';
import { alarmFiltersInitialState } from './initialState/alarmInitialState';
import { getPlatform } from './alarmHelpers';
import { transformAdvancedFilters } from './advancedFilters.helper';

// custom merge strategy to merge arrays without duplicates
const customMerge = (objValue, srcValue) => {
  if (isArray(objValue)) {
    return union(objValue, srcValue);
  }
};

const getPlatformParams = (params) => {
  const platformList = [];
  const fields = [];
  // create a copy of alarmFiltersInitialState. don't use reference
  const advancedFilters = { ...alarmFiltersInitialState };
  const publicFilter = [];
  let queryId = null;

  params?.map((item) => {
    const _platform = getPlatform(item?.api_path);

    if (_platform === 'nativePlatforms') {
      const subPlatform = item.search_params?.platforms?.[0]?.split('_')?.[0];
      platformList.push(subPlatform?.split('_')?.[0]);
      fields[subPlatform] = item?.search_params?.fields || [];
      advancedFilters[subPlatform] = transformAdvancedFilters(
        item?.search_params,
        subPlatform
      );
    } else {
      platformList.push(_platform);
      fields[_platform] = item?.search_params?.fields || [];
      advancedFilters[_platform] = transformAdvancedFilters(
        item?.search_params,
        _platform
      );
    }

    queryId = item?.search_params?.query;

    if (
      item?.search_params?.max_advert_noise &&
      !publicFilter.includes('ignoreAdvertisment')
    ) {
      publicFilter.push('ignoreAdvertisment');
    }

    if (
      item?.search_params?.source &&
      !publicFilter.includes('onlyOriginalContents')
    ) {
      publicFilter.push('onlyOriginalContents');
    }
  });

  return {
    platformList,
    fields,
    advancedFilters,
    publicFilter,
    queryId,
  };
};

export const alarmAdapter = (_data) => {
  const params = getPlatformParams(_data?.params);

  const DEFAULT_CONTENT_ALARM_MESSAGES_FIELDS = {
    news: ['time', 'title', 'relation.title'],
    instagram: ['time', 'title'],
    twitter: ['time', 'title'],
    telegram: ['time', 'title'],
    linkedin: ['time', 'title'],
    newspaper: ['time', 'title', 'resource_title'],
    bale: ['time', 'title'],
    eitaa: ['time', 'title'],
    rubika: ['time', 'title'],
    virasty: ['time', 'title'],
  };

  const DEFAULT_REPORTING_ALARM_MESSAGES_FIELDS = {
    news: ['post_count', 'agency_count'],
    instagram: ['post_count', 'user_with_at_least_one_post_count'],
    twitter: ['post_count', 'user_with_at_least_one_post_count'],
    telegram: ['post_count', 'user_with_at_least_one_post_count'],
    virasty: ['post_count', 'user_with_at_least_one_post_count'],
  };

  const mergedObject = mergeWith(
    _data?.type === 1 || _data?.type === 5
      ? DEFAULT_CONTENT_ALARM_MESSAGES_FIELDS
      : DEFAULT_REPORTING_ALARM_MESSAGES_FIELDS,
    params.fields,
    customMerge
  );

  const result = {
    step: 0,
    alarmId: _data?.id,
    type:
      _data?.type === magicNumbers.ALARM_CONTENT_SELECTED_SUBJECT ||
      _data?.type === magicNumbers.ALARM_CONTENT_SELECTED_RESOURCE
        ? 'content'
        : 'reporting',
    alarmSource: 'socialMedia',
    selectectSubjectType: 'all',
    selectedPreviewTab: '',
    contentType: undefined,
    messageFields: mergedObject,
    title: _data?.title,
    selectedHour: _data?.notify_at,
    durationType: 'everyDay',
    durationValue: [],
    selectedChannelType: _data?.receivers?.[0]?.data?.bale_channel
      ? 'bale'
      : 'telegram',
    selectedChannelId: _data?.receivers?.[0]?.id,
    advancedSettingsUpdate: false,
    advancedSettingsDelete: false,
    advancedSettingsDeleteIDs: [],
    channels: {
      data: [],
      loading: false,
      error: null,
    },
    addChannel: {
      loading: false,
      error: null,
    },
    getAlarmRequest: {
      loading: false,
      error: null,
    },
    loading: false,
    error: null,
    apps: {
      loading: false,
      error: null,
      data: [],
    },
    otherApps: {
      loading: false,
      error: null,
      data: [],
    },
  };

  if (_data?.type === magicNumbers.ALARM_CONTENT_SELECTED_SUBJECT) {
    result.contentType = 'subject';
  } else if (_data?.type === magicNumbers.ALARM_REPORTING_STATISTICAL) {
    result.reportType = 'statistical';
  } else if (_data?.type === magicNumbers.ALARM_REPORTING_NEGATIVE_ISSUES) {
    result.reportType = 'negativeIssues';
  } else if (_data?.type === magicNumbers.ALARM_REPORTING_TRENDING) {
    result.reportType = 'trending';
  } else if (_data?.type === magicNumbers.ALARM_CONTENT_SELECTED_RESOURCE) {
    result.contentType = 'resource';
  }

  if (
    _data?.type === magicNumbers.ALARM_REPORTING_STATISTICAL ||
    _data?.type === magicNumbers.ALARM_CONTENT_SELECTED_SUBJECT ||
    _data?.type === magicNumbers.ALARM_CONTENT_SELECTED_RESOURCE
  ) {
    result.selectedSubjectId = params.queryId;
    result.selectedPlatforms = params.platformList;
    result.publicFilter = params.publicFilter;
    result.filters = params.advancedFilters;
  }

  if (_data?.type === magicNumbers.ALARM_REPORTING_NEGATIVE_ISSUES) {
    result.selectedSubjectId = _data?.params[0]?.search_params?.query;
  }

  if (_data?.notify_days?.length > 0) {
    result.durationType = 'weekDays';
    result.durationValue = _data?.notify_days;
  }

  if (_data?.notify_duration > 1) {
    result.durationType = 'repeatedDays';
    result.durationValue = [_data?.notify_duration];
  }

  if (_data?.ai_summary_settings) {
    const summarySettings = _data?.ai_summary_settings;

    result.hasActiveAiSummary = true;

    if (summarySettings.interval) {
      result.durationType =
        summarySettings.interval === 'weekly' ? 'weekDays' : 'everyDay';
    }
    if (summarySettings.time) {
      result.selectedHour = summarySettings.time;
    }
    if (summarySettings.days) {
      result.durationValue = summarySettings.days;
    }
  }

  if (!_data?.ai_summary_settings) {
    result.hasActiveAiSummary = false;
  }

  _data?.advanced_settings?.forEach((item) => {
    if (item.type === 'update_entities') {
      result.advancedSettingsUpdate = item.is_active;
    }

    if (item.type === 'delete_message') {
      result.advancedSettingsDelete = item.is_active;
      result.advancedSettingsDeleteIDs = item.extra || [];
    }
  });

  if (_data?.params?.[0]?.api_path === '/cafebazaar/comments') {
    result.alarmSource = 'appStore';
    result.selectedAppIds =
      _data?.params?.[0]?.search_params?.application_ids || [];
    result.selectedPlatforms = ['cafeBazaar'];
  }

  return result;
};
